import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, CheckCircle2, Zap } from 'lucide-react';
import Tooltip from './Tooltip';

const getStatusConfig = (status) => {
  switch (status) {
    case 'red':
      return {
        color: '#ef4444',
        text: 'text-rose-400',
        label: 'critical',
        icon: AlertCircle
      };
    case 'yellow':
      return {
        color: '#f59e0b',
        text: 'text-amber-400',
        label: 'risk',
        icon: Zap
      };
    case 'green':
    default:
      return {
        color: '#10b981',
        text: 'text-emerald-400',
        label: 'healthy',
        icon: CheckCircle2
      };
  }
};

const ScrumTrafficLight = ({ health, t, endDate, daysRemaining }) => {
  const { status = 'green', message = '', progressPercent = 0, timeElapsedPercent = 0 } = health || {};
  const config = getStatusConfig(status);
  const Icon = config.icon;
  const [isEasterEgg, setIsEasterEgg] = React.useState(false);
  const [activeEggLight, setActiveEggLight] = React.useState(2); // Start with Green (bottom)
  const hoverTimer = React.useRef(null);
  const rotationInterval = React.useRef(null);

  const handleMouseEnter = () => {
    hoverTimer.current = setTimeout(() => {
      setIsEasterEgg(true);
      // Start color rotation
      let current = 2;
      rotationInterval.current = setInterval(() => {
        current = current === 0 ? 2 : current - 1;
        setActiveEggLight(current);
      }, 800);
    }, 6000);
  };

  const handleMouseLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    if (rotationInterval.current) clearInterval(rotationInterval.current);
    setIsEasterEgg(false);
  };

  return (
    <div className="flex items-center flex-shrink-0">
      <Tooltip 
        delay={200}
        content={(
          <div className="flex flex-col gap-2 p-1 min-w-[200px]">
            <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{t('sprintHealth')}</span>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-black uppercase tracking-widest ${config.text}`}>
                  {t(config.label) || config.label}
                </span>
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }} />
              </div>
            </div>
            
            <p className="text-xs text-slate-200 font-medium leading-relaxed py-1">
              {message}
            </p>
            
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/5">
               <div className="flex flex-col">
                  <span className="text-[8px] font-black text-slate-500 uppercase tracking-tighter">{t('progress')}</span>
                  <span className="text-[11px] font-black text-white">{Math.round(progressPercent)}%</span>
               </div>
               <div className="flex flex-col">
                  <span className="text-[8px] font-black text-slate-500 uppercase tracking-tighter">{t('timeElapsed')}</span>
                  <span className="text-[11px] font-black text-white">{Math.round(timeElapsedPercent)}%</span>
               </div>
            </div>
          </div>
        )}
      >
        <div 
          className="flex items-center gap-2.5 px-3 py-1 cursor-help transition-all group/traffic relative min-h-[4.5rem] justify-center"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* The column which contains the moving traffic light AND the static grass */}
          <div className="relative flex flex-col items-center w-6 h-full">
            {/* Traffic Light Body */}
            <motion.div 
              animate={{ y: isEasterEgg ? -25 : 0 }}
              className="flex flex-col items-center gap-1 py-1 px-1 bg-black/80 rounded-full border-[1.5px] border-amber-500/80 relative z-20 shadow-lg"
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              {['red', 'yellow', 'green'].map((lightColor, index) => {
                const isActive = isEasterEgg ? (activeEggLight === index) : (status === lightColor);
                const itemConfig = getStatusConfig(lightColor);
                
                return (
                  <div key={lightColor} className="relative">
                    <motion.div 
                      initial={false}
                      animate={{ 
                        backgroundColor: isActive ? itemConfig.color : 'rgba(15, 23, 42, 0.4)',
                        opacity: isActive ? 1 : 0.5,
                        borderColor: isActive ? itemConfig.color : 'rgba(251, 191, 36, 0.8)'
                      }}
                      className="w-1.5 h-1.5 rounded-full border-[1.5px] transition-all duration-300"
                      style={{ 
                        boxShadow: isActive ? `0 0 12px 2px ${itemConfig.color}` : 'none'
                      }}
                    />
                    
                    {/* Constant Pulse Components - Opacity controlled for stability */}
                    <div className={`absolute inset-0 pointer-events-none transition-opacity duration-500 ${isActive ? 'opacity-100' : 'opacity-0'}`}>
                      {/* Inner Sharp Pulse */}
                      <motion.div
                        initial={{ scale: 1, opacity: 0.8 }}
                        animate={isActive ? { scale: 2.2, opacity: 0 } : {}}
                        transition={{ duration: 1, repeat: Infinity, ease: "easeOut" }}
                        className="absolute inset-0 rounded-full border-[1.5px]"
                        style={{ borderColor: itemConfig.color }}
                      />
                      {/* Outer Sharp Ring */}
                      <motion.div
                        initial={{ scale: 1, opacity: 0.4 }}
                        animate={isActive ? { scale: 3.5, opacity: 0 } : {}}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
                        className="absolute inset-0 rounded-full border"
                        style={{ borderColor: itemConfig.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </motion.div>

            {/* Leg (Post) - Now a sibling, layered behind */}
            <AnimatePresence>
              {isEasterEgg && (
                <motion.div 
                  initial={{ height: 0, y: 0 }}
                  animate={{ height: 28, y: -25 }}
                  exit={{ height: 0, y: 0 }}
                  className="absolute top-[28px] left-1/2 -translate-x-1/2 w-[5px] bg-slate-900 border-x border-amber-500/50 z-10 shadow-xl"
                />
              )}
            </AnimatePresence>

            {/* Grass Base - Static at the bottom of the container */}
            <AnimatePresence>
              {isEasterEgg && (
                <motion.div 
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    className="absolute bottom-1 left-1/2 -translate-x-1/2 flex items-end justify-center pointer-events-none"
                    style={{ zIndex: 30 }}
                >
                    <div className="relative flex flex-col items-center">
                        <div className="w-10 h-1 bg-black/60 rounded-[50%] blur-[2px] mb-[-1px]" />
                        <div className="flex items-end gap-[0.2px] justify-center absolute -bottom-0.5">
                          {[...Array(16)].map((_, i) => {
                            const hBase = [3, 6, 4, 7, 5, 8, 4, 6][i % 8];
                            const heightVar = hBase * (0.6 + Math.random() * 0.8); // High variability
                            
                            return (
                              <motion.div 
                                key={i}
                                initial={{ height: 0 }}
                                animate={{ 
                                  height: heightVar,
                                  rotate: [-(8 + i % 6), (8 + i % 6)],
                                }}
                                transition={{ 
                                  rotate: { 
                                    duration: 3 + (i * 0.2), 
                                    repeat: Infinity, 
                                    ease: "easeInOut", 
                                    repeatType: "mirror" 
                                  },
                                  height: { duration: 0.6, delay: i * 0.03 }
                                }}
                                className="w-[1.5px] rounded-full origin-bottom shadow-sm"
                                style={{ 
                                  background: i % 3 === 0 
                                    ? 'linear-gradient(to top, #065f46, #10b981, #bef264)' 
                                    : i % 3 === 1
                                    ? 'linear-gradient(to top, #166534, #22c55e, #84cc16)'
                                    : 'linear-gradient(to top, #14532d, #16a34a, #d9f99d)',
                                  opacity: 0.8 + (Math.random() * 0.2),
                                  marginLeft: `${(i % 5) - 2}px`
                                }}
                              />
                            );
                          })}
                        </div>
                    </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex flex-col ml-1">
            <div className="flex items-center gap-2">
              <span 
                className={`text-[9px] font-black uppercase tracking-[0.2em] ${config.text} transition-colors`}
              >
                {t('sprintStatus')}
              </span>
              <Icon size={10} className={`${config.text}`} />
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[11px] font-display font-black text-white tracking-tight uppercase">
                {t(config.label) || config.label}
              </span>
            </div>
          </div>
        </div>
      </Tooltip>

      {(endDate || daysRemaining !== undefined) && (
        <div className="flex items-center gap-3 ml-4 pl-4 border-l border-white/10 h-10 flex-shrink-0">
          {endDate && (
            <div className="flex flex-col flex-shrink-0">
              <span className="text-[8px] font-black text-slate-500 uppercase tracking-tight mb-0.5">{t('endDate')}</span>
              <span className="text-[11px] font-black text-slate-300 uppercase leading-none whitespace-nowrap">{new Date(endDate).toLocaleDateString(t('locale') || 'es-ES', { day: '2-digit', month: 'short' })}</span>
            </div>
          )}
          {daysRemaining !== undefined && (
            <div className={`flex flex-col px-2.5 py-1.5 rounded-xl border flex-shrink-0 ${
              daysRemaining <= 2 ? 'bg-rose-500/10 border-rose-500/20 text-rose-500' :
              daysRemaining <= 5 ? 'bg-amber-500/10 border-amber-500/20 text-amber-500' :
              'bg-emerald-500/10 border-emerald-500/20 text-emerald-500'
            }`}>
              <span className="text-[8px] font-black uppercase tracking-tight leading-none opacity-70 mb-0.5 whitespace-nowrap">{t('remaining')}</span>
              <div className="flex items-center gap-1">
                <span className="text-[12px] font-black leading-none">{daysRemaining}</span>
                <span className="text-[7px] font-black uppercase leading-none whitespace-nowrap">
                  {daysRemaining === 1 ? t('day') : t('days')}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScrumTrafficLight;
